import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAppState } from '@/contexts/AppStateContext';

export interface AutoReplySettings {
  active: boolean;
  mode: 'manual' | 'hybrid' | 'full_auto';
  delay_min: number;
  delay_max: number;
}

export interface Establishment {
  id: string;
  user_id: string;
  name: string;
  address: string | null;
  city: string | null;
  logo_url: string | null;
  google_place_id: string | null;
  google_location_name?: string | null;
  google_reviews_link: string | null;
  // Multi-platform URLs
  yelp_url: string | null;
  tripadvisor_url: string | null;
  facebook_url: string | null;
  trustpilot_url: string | null;
  trustpilot_business_unit_id?: string | null;
  // Connection status
  google_connected?: boolean;
  facebook_connected?: boolean;
  trustpilot_connected?: boolean;
  yelp_connected?: boolean;
  tripadvisor_connected?: boolean;
  // Platform enabled flags - controls which platforms appear in UI
  google_enabled?: boolean;
  facebook_enabled?: boolean;
  trustpilot_enabled?: boolean;
  yelp_enabled?: boolean;
  tripadvisor_enabled?: boolean;
  // Timezone for AI reply scheduling (IANA format, e.g., "America/New_York")
  timezone?: string;
  // Auto-reply settings
  auto_reply_settings?: AutoReplySettings;
  // Platform ratings (official rating from the platform)
  current_rating_google?: number | null;
  current_rating_facebook?: number | null;
  current_rating_tripadvisor?: number | null;
  current_rating_yelp?: number | null;
  current_rating_trustpilot?: number | null;
  // Review counts per platform
  review_count_google?: number | null;
  review_count_facebook?: number | null;
  review_count_tripadvisor?: number | null;
  review_count_yelp?: number | null;
  review_count_trustpilot?: number | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface EstablishmentContextType {
  establishments: Establishment[];
  selectedEstablishment: Establishment | null;
  setSelectedEstablishment: (establishment: Establishment | null) => void;
  loading: boolean;
  canAddMore: boolean;
  maxEstablishments: number;
  hasMultiLocation: boolean;
  plan: string;
  createEstablishment: (data: Partial<Establishment>) => Promise<{ data?: Establishment; error?: string }>;
  updateEstablishment: (id: string, data: Partial<Establishment>) => Promise<{ success?: boolean; error?: string }>;
  deleteEstablishment: (id: string) => Promise<{ success?: boolean; error?: string }>;
  setAsDefault: (id: string) => Promise<{ success?: boolean; error?: string }>;
  refetch: () => Promise<void>;
}

const EstablishmentContext = createContext<EstablishmentContextType | null>(null);

// 🔧 Fetch function for React Query
const fetchEstablishmentsQuery = async (userId: string): Promise<Establishment[]> => {
  const { data, error } = await supabase
    .from('establishments')
    .select('*')
    .eq('user_id', userId)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) throw error;
  
  // Map the data to handle JSONB auto_reply_settings correctly
  return (data || []).map(est => ({
    ...est,
    auto_reply_settings: est.auto_reply_settings as unknown as AutoReplySettings | undefined,
  })) as unknown as Establishment[];
};

export function EstablishmentProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, subscriptionPlan } = useAppState();
  const queryClient = useQueryClient();
  const [selectedEstablishment, setSelectedEstablishment] = useState<Establishment | null>(null);
  const prevUserIdRef = useRef<string | null>(null);

  // 🧹 Clear stale cache when user changes (account switch)
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (prevUserIdRef.current && currentUserId && prevUserIdRef.current !== currentUserId) {
      console.log('[Establishments] 🔄 User changed, clearing stale cache...');
      queryClient.removeQueries({ queryKey: ['establishments', prevUserIdRef.current] });
      setSelectedEstablishment(null);
    }
    prevUserIdRef.current = currentUserId;
  }, [user?.id, queryClient]);

  // Plan-based limits
  const plan = subscriptionPlan || 'starter';
  const hasMultiLocation = plan === 'business';
  const maxEstablishments = hasMultiLocation ? 5 : 1;

  // 🔧 Use React Query for caching establishments - no refetch on window focus
  const { data: establishments = [], isLoading: loading, refetch: refetchQuery } = useQuery({
    queryKey: ['establishments', user?.id],
    queryFn: () => fetchEstablishmentsQuery(user!.id),
    enabled: !!user && isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10,   // 10 minutes in cache
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Auto-select default establishment when data loads
  // Use useEffect to avoid state updates during render
  useEffect(() => {
    if (establishments.length === 0) return;
    
    if (!selectedEstablishment) {
      // No selection yet - pick the default or first
      const defaultEst = establishments.find(e => e.is_default) || establishments[0];
      if (defaultEst) {
        setSelectedEstablishment(defaultEst);
      }
    } else {
      // Update selected with fresh data if it exists (only if ID matches but data changed)
      const updatedSelected = establishments.find(e => e.id === selectedEstablishment.id);
      if (updatedSelected && JSON.stringify(updatedSelected) !== JSON.stringify(selectedEstablishment)) {
        setSelectedEstablishment(updatedSelected);
      }
    }
  }, [establishments, selectedEstablishment]);

  const canAddMore = establishments.length < maxEstablishments;

  // Wrapper to convert refetch to Promise<void>
  const refetch = async () => {
    await refetchQuery();
  };

  const createEstablishment = async (data: Partial<Establishment>) => {
    if (!user) return { error: 'Non authentifié' };
    if (!canAddMore) return { error: `Limite de ${maxEstablishments} établissement(s) atteinte` };

    const isFirst = establishments.length === 0;
    
    const { data: newEst, error } = await supabase
      .from('establishments')
      .insert({
        user_id: user.id,
        name: data.name || 'Nouvel établissement',
        address: data.address,
        city: data.city,
        logo_url: data.logo_url,
        google_place_id: data.google_place_id,
        google_reviews_link: data.google_reviews_link,
        is_default: isFirst,
      })
      .select()
      .single();

    if (!error && newEst) {
      await refetch();
      const mappedEst = { ...newEst, auto_reply_settings: newEst.auto_reply_settings as unknown as AutoReplySettings | undefined } as unknown as Establishment;
      if (isFirst) {
        setSelectedEstablishment(mappedEst);
      }
      return { data: mappedEst };
    }
    
    return { error: error?.message || 'Erreur lors de la création' };
  };

  const updateEstablishment = async (id: string, data: Partial<Establishment>) => {
    const { error } = await supabase
      .from('establishments')
      .update({
        name: data.name,
        address: data.address,
        city: data.city,
        logo_url: data.logo_url,
        google_place_id: data.google_place_id,
        google_reviews_link: data.google_reviews_link,
        // Multi-platform URLs
        yelp_url: data.yelp_url,
        tripadvisor_url: data.tripadvisor_url,
        facebook_url: data.facebook_url,
        trustpilot_url: data.trustpilot_url,
      })
      .eq('id', id);

    if (!error) {
      await refetch();
      // Invalidate trust score query to force refresh
      queryClient.invalidateQueries({
        queryKey: ['establishment-trust-score', id],
      });
      return { success: true };
    }
    
    return { error: error.message };
  };

  const deleteEstablishment = async (id: string) => {
    if (establishments.length <= 1) {
      return { error: 'Vous devez conserver au moins un établissement' };
    }

    const { error } = await supabase
      .from('establishments')
      .delete()
      .eq('id', id);

    if (!error) {
      if (selectedEstablishment?.id === id) {
        const remaining = establishments.filter(e => e.id !== id);
        setSelectedEstablishment(remaining[0] || null);
      }
      await refetch();
      return { success: true };
    }
    
    return { error: error.message };
  };

  const setAsDefault = async (id: string) => {
    // Remove default from all
    await supabase
      .from('establishments')
      .update({ is_default: false })
      .eq('user_id', user?.id);

    // Set new default
    const { error } = await supabase
      .from('establishments')
      .update({ is_default: true })
      .eq('id', id);

    if (!error) {
      await refetch();
      return { success: true };
    }
    
    return { error: error.message };
  };

  return (
    <EstablishmentContext.Provider
      value={{
        establishments,
        selectedEstablishment,
        setSelectedEstablishment,
        loading,
        canAddMore,
        maxEstablishments,
        plan,
        hasMultiLocation,
        createEstablishment,
        updateEstablishment,
        deleteEstablishment,
        setAsDefault,
        refetch,
      }}
    >
      {children}
    </EstablishmentContext.Provider>
  );
}

export function useEstablishments() {
  const context = useContext(EstablishmentContext);
  if (!context) {
    throw new Error('useEstablishments must be used within an EstablishmentProvider');
  }
  return context;
}